import type { MultiPolygon, Polygon } from 'geojson';

import type { Opportunity } from '../../shared/opportunity';
import { pointInShape } from './traveltime';
import { eventPoint, minutesTo, type LatLng, type TravelCity, type TravelMode } from './walkshed';

/**
 * Minutes from the origin to each event, from the best source there is
 * (contract §17). The filter, the counts, Nearest first and the card's travel
 * line all read this one object, so they can never disagree.
 *
 *   1. `traveltime`: TravelTime's time-filter, routed on real streets and
 *      timetables. The truth.
 *   2. `polygon`: time-filter failed, but a time-map arrived: an event inside
 *      the drawn isochrone is within its minutes, outside is beyond them. The
 *      minutes shown are the geometric estimate held to that side of the line.
 *   3. `geometric`: neither: §12's superellipse maths.
 *
 * Only the first is exact; the other two are marked approximate ("≈").
 */

export type TravelSource = 'traveltime' | 'polygon' | 'geometric';

/** Known to be further than the time-filter looks (90 minutes): not "unknown". */
export const UNREACHABLE = Number.POSITIVE_INFINITY;

export interface TravelTimes {
  source: TravelSource;
  mode: TravelMode;
  /**
   * Minutes to the event; `UNREACHABLE` when it is further than 90; null when
   * the event has nowhere to measure to (online, or no address or neighborhood).
   */
  minutes(event: Opportunity): number | null;
  /** Whether the event's minutes are an estimate: the card says "≈". */
  approximate(event: Opportunity): boolean;
}

/** The id an event's point goes by in a time-filter request: its coordinates, so events at one spot share it. */
export const pointId = ([lat, lng]: LatLng) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

/** An isochrone as the map draws it, and the minutes it was drawn for. */
export interface Shed {
  geometry: MultiPolygon | Polygon;
  minutes: number;
  /** Drawn from the geometric maths, not from TravelTime. */
  approximate: boolean;
}

interface Sources {
  origin: LatLng;
  mode: TravelMode;
  city: TravelCity;
  /** Time-filter's answer for this origin and mode, keyed by `pointId`. */
  filtered?: ReadonlyMap<string, number | null> | null;
  /** A TravelTime isochrone for this origin and mode (not an approximate one). */
  shed?: Shed | null;
}

export function makeTravelTimes({ origin, mode, city, filtered, shed }: Sources): TravelTimes {
  const geometric = (at: LatLng) => minutesTo(origin, at, mode, city);
  const realShed = shed && !shed.approximate ? shed : null;
  const source: TravelSource = filtered ? 'traveltime' : realShed ? 'polygon' : 'geometric';

  const memo = new WeakMap<Opportunity, { minutes: number | null; approximate: boolean }>();
  const measure = (event: Opportunity) => {
    let m = memo.get(event);
    if (m) return m;
    const at = eventPoint(event, city);
    if (!at) m = { minutes: null, approximate: false };
    else if (filtered) {
      const id = pointId(at.point);
      // An edited address TravelTime wasn't asked about: estimate that one alone.
      m = filtered.has(id)
        ? { minutes: filtered.get(id) ?? UNREACHABLE, approximate: false }
        : { minutes: geometric(at.point), approximate: true };
    } else if (realShed) {
      const estimate = geometric(at.point);
      const inside = pointInShape(at.point, realShed.geometry);
      // Held to the polygon's side of its own minutes, so the card never
      // contradicts the shape on the map.
      m = {
        minutes: inside ? Math.min(estimate, realShed.minutes) : Math.max(estimate, realShed.minutes + 1),
        approximate: true,
      };
    } else m = { minutes: geometric(at.point), approximate: true };
    memo.set(event, m);
    return m;
  };

  return {
    source,
    mode,
    minutes: (event) => measure(event).minutes,
    approximate: (event) => measure(event).approximate,
  };
}

/** Every distinct event point, for one time-filter request. */
export function eventPoints(events: Iterable<Opportunity>, city: TravelCity): { id: string; point: LatLng }[] {
  const out = new Map<string, LatLng>();
  for (const e of events) {
    const at = eventPoint(e, city);
    if (at) out.set(pointId(at.point), at.point);
  }
  return [...out].map(([id, point]) => ({ id, point }));
}
